import { ApiError } from '@/api/api.error';
import { Components } from '@/api/generated/api';
import { ErrorTypes, ReportType } from '@/api/generated/api.enum';
import {
  closeReport as closeReportApi,
  createReportsForClass,
  createReportsForClassAsync,
  deleteReport as deleteReportApi,
  deleteReportSubject as deleteReportSubjectApi,
  fetchCanReportBeCreated,
  fetchNoteSubjectIds,
  fetchObservedSubjectIds,
  fetchReport,
  fetchReportsForClass,
  updateReport as updateReportApi,
  updateReportComment as updateReportCommentApi,
  updateReportCompetence as updateReportCompetenceApi,
  updateReportSubject as updateReportSubjectApi,
} from '@/api/resources/report.api';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useCurrentReportGroup } from '@/composables/useCurrentReportGroup';
import { useAsyncOperations } from '@/composables/useAsyncOperations';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useReportGroup } from '@/composables/useReportGroup';
import { useToast } from '@/composables/useToast';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, h, Ref, ref, watch, type ComputedRef } from 'vue';
import { Translation, useI18n } from 'vue-i18n';
import { useAnalytics } from '../analytics/useAnalytics';
import { AnalyticsEvents } from '@/services/analytics/events';
import { FilterOperationType, IFilter } from '@chax-at/prisma-filter-common';
import { IReportFilter } from 'common/interface/filter.interface';
import { IReportConfiguration } from '@/interfaces';
import { useReportConfigurationStore } from '@/stores/reportConfigurationStore';
import { useTrialUpsellStore } from '@/stores/useTrialUpsellStore';
import { useRouter } from 'vue-router';
import { reportKeys } from './report.keys';
import { reportGroupKeys } from '../reportGroup/reportGroup.keys';
import { useRefreshSetupChecklist } from '../setupChecklist/useRefreshSetupChecklist';

export interface BulkCreateReportsAsyncResult {
  reportIds: number[];
  failedStudentIds: number[];
  schoolClassId: number;
  groupId: number | null;
  createdStudentIds: number[];
}

export enum EReportSortOption {
  CREATED_AT_DESC = 'createdAtDesc',
  CREATED_AT_ASC = 'createdAtAsc',
  LAST_NAME_ASC = 'lastNameAsc',
  REPORT_NAME_ASC = 'reportNameAsc',
}

export type GetObservedSubjectIdsParams = Omit<Components.Schemas.GetObservedSubjectIdsDto, 'schoolClassId'>;

interface IReportComposable {
  /** @returns false when the report still needs a cover picture before it can be completed */
  closeReport: (reportId: number) => Promise<boolean>;
  createBulkReports: (
    classId: number,
    dto: Components.Schemas.CreateBulkReportsDto,
  ) => Promise<Components.Schemas.BaseReportDto[]>;
  createBulkReportsAsync: (classId: number, dto: Components.Schemas.CreateBulkReportsDto) => Promise<void>;
  confirmCloseReport: (ambiguousPerformanceLevelAmount: number) => Promise<boolean>;
  deleteReport: (reportId: number) => Promise<boolean>;
  getReports: (classId: number) => Promise<Components.Schemas.PaginatedReportResponseDto>;
  getReport: (reportId: number) => Promise<Components.Schemas.ReportDto>;
  reports: ComputedRef<Components.Schemas.BaseReportDto[]>;
  totalReports: ComputedRef<number>;
  isLoading: ComputedRef<boolean>;
  currentPage: Ref<number>;
  pageSize: Ref<number>;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  updateReport: (reportId: number, dto: Components.Schemas.UpdateReportDto) => Promise<void>;
  updateReportComment: (reportId: number, dto: Components.Schemas.UpdateReportGeneralCommentDto) => Promise<void>;
  updateReportCompetence: (params: {
    reportId: number;
    competenceId: number;
    schoolClassId: number;
    dto: Components.Schemas.UpdateReportCompetenceDto;
  }) => Promise<void>;
  updateReportSubject: (params: {
    reportId: number;
    reportSubjectId: number;
    dto: Components.Schemas.UpdateReportSubjectDto;
    showToast?: boolean;
  }) => Promise<void>;
  refresh: () => Promise<void>;

  searchText: Ref<string>;
  statusFilter: Ref<Components.Schemas.ReportStatus | null>;
  sortOption: Ref<EReportSortOption>;
  setSearchText: (value: string) => void;
  setStatusFilter: (value: Components.Schemas.ReportStatus | null) => void;
  setSortOption: (value: EReportSortOption) => void;
  groupIdFilter: Ref<number | null>;
  setGroupIdFilter: (value: number | null) => void;
  studentIdFilter: Ref<number | null>;
  setStudentIdFilter: (value: number | null) => void;
  getDefaultReportConfiguration: (type: Components.Schemas.ReportType) => IReportConfiguration;
  getSelectedReportConfiguration: (type: Components.Schemas.ReportType) => IReportConfiguration;
  deleteReportSubject: (reportId: number, reportSubjectId: number) => Promise<boolean>;
  getObservedSubjectIds: (params: GetObservedSubjectIdsParams) => Promise<number[]>;
  getNoteSubjectIds: (params: GetObservedSubjectIdsParams) => Promise<Components.Schemas.NoteSubjectIdsDto>;
  canReportBeCreated: () => Promise<Components.Schemas.EReportDataStatus | null>;
}

export const useReport = (): IReportComposable => {
  const { createSuccessToast, editSuccessToast, catchError, deleteSuccessToast, showError } = useToast();
  const { currentClass } = useCurrentClass();
  const { refresh: refreshReportGroups } = useReportGroup();
  const { startAsyncOperation } = useAsyncOperations();
  const { confirm } = useConfirmDialog();
  const { t } = useI18n();
  const { push } = useRouter();
  const { trackEvent } = useAnalytics();
  const reportConfigurationStore = useReportConfigurationStore();
  const trialUpsellStore = useTrialUpsellStore();
  const { setCurrentReportGroupId } = useCurrentReportGroup();
  const currentPage = ref(1);
  const pageSize = ref(50);
  const searchText = ref('');
  const statusFilter = ref<Components.Schemas.ReportStatus | null>(null);
  const sortOption = ref<EReportSortOption>(EReportSortOption.CREATED_AT_DESC);
  const groupIdFilter = ref<number | null>(null);
  const studentIdFilter = ref<number | null>(null);

  const getDefaultReportConfiguration = (type: Components.Schemas.ReportType): IReportConfiguration =>
    reportConfigurationStore.getDefaultConfiguration(type);
  const getSelectedReportConfiguration = (type: Components.Schemas.ReportType): IReportConfiguration =>
    reportConfigurationStore.getSelectedConfiguration(type);

  const buildFilter = (page = currentPage.value, limit = pageSize.value): IFilter<IReportFilter> => {
    const offset = (page - 1) * limit;

    const filter: IFilter<IReportFilter> = {
      limit,
      offset,
      filter: [],
      order: [],
    };

    if (groupIdFilter.value != null) {
      filter.filter!.push({
        field: 'groupId',
        type: FilterOperationType.Eq,
        value: groupIdFilter.value,
      });
    }

    if (studentIdFilter.value != null) {
      filter.filter!.push({
        field: 'originalStudentId',
        type: FilterOperationType.Eq,
        value: studentIdFilter.value,
      });
    }

    if (statusFilter.value) {
      filter.filter!.push({
        field: 'status',
        type: FilterOperationType.Eq,
        value: statusFilter.value,
      });
    }

    switch (sortOption.value) {
      case EReportSortOption.CREATED_AT_ASC:
        filter.order!.push({
          field: 'createdAt',
          dir: 'asc',
        });
        break;
      case EReportSortOption.LAST_NAME_ASC:
        filter.order!.push({
          field: 'studentLastName',
          dir: 'asc',
        });
        break;
      case EReportSortOption.REPORT_NAME_ASC:
        filter.order!.push({
          field: 'name',
          dir: 'asc',
        });
        break;
      case EReportSortOption.CREATED_AT_DESC:
      default:
        filter.order!.push({
          field: 'createdAt',
          dir: 'desc',
        });
        break;
    }

    return filter;
  };

  const getReports = async (
    classId?: number,
    page = currentPage.value,
    limit = pageSize.value,
  ): Promise<Components.Schemas.PaginatedReportResponseDto> => {
    if (!classId) {
      return {
        success: true,
        rows: [],
        count: 0,
      };
    }

    const $filter = buildFilter(page, limit);

    return fetchReportsForClass({
      schoolClassId: classId,
      $filter,
      search: searchText.value.trim() || undefined,
    });
  };

  const getReport = async (reportId: number): Promise<Components.Schemas.ReportDto> => {
    return fetchReport({ reportId });
  };
  const reportListQueryKey = computed(() =>
    reportKeys.list({
      classId: currentClass.value?.id,
      currentPage: currentPage.value,
      pageSize: pageSize.value,
      searchText: searchText.value,
      status: statusFilter.value,
      sortOption: sortOption.value,
      groupId: groupIdFilter.value,
      studentId: studentIdFilter.value,
    }),
  );
  const queryClient = useQueryClient();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();
  const {
    data,
    isFetching: isFetchingReports,
    isLoading: isLoadingReports,
  } = useQueryWithToast({
    queryKey: reportListQueryKey,
    queryFn: () => getReports(currentClass.value?.id, currentPage.value, pageSize.value),
  });

  const reports = computed<Components.Schemas.BaseReportDto[]>(() => {
    return data.value?.rows ?? [];
  });

  const totalReports = computed<number>(() => {
    return data.value?.count ?? 0;
  });

  const isLoading = computed(() => isLoadingReports.value || isFetchingReports.value);

  const setPage = (page: number): void => {
    if (page < 1) return;
    currentPage.value = page;
  };

  const setPageSize = (size: number): void => {
    pageSize.value = size;
    currentPage.value = 1;
  };

  const setSearchText = (value: string): void => {
    searchText.value = value;
    currentPage.value = 1;
  };

  const setStatusFilter = (value: Components.Schemas.ReportStatus | null): void => {
    statusFilter.value = value;
    currentPage.value = 1;
  };

  const setSortOption = (value: EReportSortOption): void => {
    sortOption.value = value;
  };

  const setGroupIdFilter = (value: number | null): void => {
    groupIdFilter.value = value;
    currentPage.value = 1;
  };

  const setStudentIdFilter = (value: number | null): void => {
    studentIdFilter.value = value;
    currentPage.value = 1;
  };

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: reportKeys.classLists(currentClass.value?.id) });
  };

  const createBulkReports = async (
    classId: number,
    dto: Components.Schemas.CreateBulkReportsDto,
  ): Promise<Components.Schemas.BaseReportDto[]> => {
    const result = await catchError(async () => {
      const created = await createReportsForClass({ schoolClassId: classId, dto });
      trialUpsellStore.recordReportsCreated(created.length);
      trialUpsellStore.requestEligibilityCheck();
      await refresh();
      await refreshReportGroups();
      await refreshSetupChecklist();
      createSuccessToast();
      trackEvent(AnalyticsEvents.report.bulkCreated);
      return created;
    });
    return result;
  };

  const readBulkCreateReportsResult = (
    status: Components.Schemas.AsyncOperationStatusDto,
  ): BulkCreateReportsAsyncResult => {
    const result = status.result as BulkCreateReportsAsyncResult | null;
    if (result == null) {
      throw new Error('Completed report creation job did not return a result');
    }

    return result;
  };

  const createBulkReportsAsync = async (
    classId: number,
    dto: Components.Schemas.CreateBulkReportsDto,
  ): Promise<void> => {
    const configuration: IReportConfiguration = {
      coverSheet: dto.baseData.coverSheet,
      competenceType: dto.baseData.competenceType,
      assessedCompetencesOnly: dto.baseData.assessedCompetencesOnly,
      showGradeRecommendation: dto.baseData.showGradeRecommendation,
      showCompetenceLevelDescriptions: dto.baseData.showCompetenceLevelDescriptions,
      showNoteCompetences: dto.baseData.showNoteCompetences ?? true,
      summarySheet: dto.baseData.summarySheet,
    };
    reportConfigurationStore.setSelectedConfiguration(dto.baseData.type ?? ReportType.standard, configuration);

    await startAsyncOperation<BulkCreateReportsAsyncResult>({
      start: () => createReportsForClassAsync({ schoolClassId: classId, dto }),
      onCompleted: async ({ status }) => {
        const result = readBulkCreateReportsResult(status);

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: reportKeys.all }),
          queryClient.invalidateQueries({ queryKey: reportGroupKeys.all }),
          refreshSetupChecklist(),
        ]);
        trackEvent(AnalyticsEvents.report.bulkCreated);
        trialUpsellStore.recordReportsCreated(result.reportIds.length);
        trialUpsellStore.requestEligibilityCheck();

        return result;
      },
      shouldBlockFrontend: false,
      successAction: {
        label: t('general.showAll'),
        onClick: async ({ result }) => {
          if (result == null) {
            throw new Error('Completed report creation job did not return a result');
          }

          if (result.groupId != null) {
            setCurrentReportGroupId(result.groupId);
            await push({
              name: '//class/[classId]/reports/group/[reportGroupId]',
              params: {
                classId: result.schoolClassId,
                reportGroupId: result.groupId,
              },
            });
          } else {
            await push({
              name: '//class/[classId]/reports/allReports',
              params: { classId: result.schoolClassId },
            });
          }
        },
      },
      toastMessages: {
        pending: t('reports.creatingReports'),
        success: t('reports.finishedReports'),
      },
    });
  };

  const updateReport = async (reportId: number, dto: Components.Schemas.UpdateReportDto): Promise<void> => {
    await catchError(async () => {
      await updateReportApi({ reportId, dto });
      await refresh();
      editSuccessToast();
      trackEvent(AnalyticsEvents.report.updated);
    });
  };

  const updateReportSubject = async (params: {
    reportId: number;
    reportSubjectId: number;
    dto: Components.Schemas.UpdateReportSubjectDto;
    showToast?: boolean;
  }): Promise<void> => {
    const { reportId, reportSubjectId, dto, showToast = true } = params;
    await catchError(async () => {
      await updateReportSubjectApi({ reportId, reportSubjectId, dto });
      await refresh();
      if (showToast) editSuccessToast();

      trackEvent(AnalyticsEvents.report.updated);
    });
  };

  const updateReportComment = async (
    reportId: number,
    dto: Components.Schemas.UpdateReportGeneralCommentDto,
  ): Promise<void> => {
    await catchError(async () => {
      await updateReportCommentApi({ reportId, dto });
      await refresh();
      trackEvent(AnalyticsEvents.report.updated);
      editSuccessToast();
    });
  };

  const updateReportCompetence = async (params: {
    reportId: number;
    competenceId: number;
    schoolClassId: number;
    dto: Components.Schemas.UpdateReportCompetenceDto;
  }): Promise<void> => {
    await catchError(async () => {
      await updateReportCompetenceApi(params);
      await refresh();
      editSuccessToast();
      trackEvent(AnalyticsEvents.report.updated);
    });
  };

  const confirmCloseReport = async (ambiguousPerformanceLevelAmount: number): Promise<boolean> => {
    const title = ambiguousPerformanceLevelAmount
      ? t('reports.confirmCloseWithAmbiguousPerformanceLevelsTitle')
      : t('reports.completeReport');
    const description = ambiguousPerformanceLevelAmount
      ? () =>
          h(
            Translation,
            {
              keypath: 'reports.confirmCloseWithAmbiguousPerformanceLevels',
              plural: ambiguousPerformanceLevelAmount,
              scope: 'global',
            },
            {
              ambiguousCompetences: () =>
                h('strong', t('reports.ambiguousCompetenceCount', { count: ambiguousPerformanceLevelAmount })),
            },
          )
      : t('reports.confirmCloseDescription');

    const confirmButtonLabel = ambiguousPerformanceLevelAmount ? null : t('reports.completeReport');
    const confirmed = await confirm({
      confirmButtonLabel,
      declineButtonLabel: t('general.continueEditing'),
      description,
      size: 'lg',
      title,
    });

    return confirmed;
  };

  const closeReport = async (reportId: number): Promise<boolean> => {
    try {
      await closeReportApi({ reportId });
    } catch (error) {
      // The caller resolves a missing cover picture through a dialog instead of an error toast.
      if (error instanceof ApiError && error.errorType === ErrorTypes.REPORT_COVER_IMAGE_MISSING) return false;
      showError(error);
      throw error;
    }

    await refresh();
    editSuccessToast();
    trackEvent(AnalyticsEvents.report.closed);
    return true;
  };

  const deleteReport = async (reportId: number): Promise<boolean> => {
    const confirmed = await confirm({
      title: t('reports.delete'),
      description: t('reports.deleteConfirmDescription'),
      confirmButtonLabel: t('general.delete'),
      declineButtonLabel: t('general.cancel'),
      theme: 'danger',
    });
    if (!confirmed) return false;

    await catchError(async () => {
      await deleteReportApi({ reportId });
      await refresh();
      deleteSuccessToast();
      trackEvent(AnalyticsEvents.report.deleted);
    });
    return true;
  };

  watch(
    () => currentClass.value?.id,
    () => {
      currentPage.value = 1;
    },
  );

  const deleteReportSubject = async (reportId: number, reportSubjectId: number): Promise<boolean> => {
    const confirmed = await confirm({
      title: t('reports.deletePage'),
      description: t('reports.deletePageConfirmDescription'),
      confirmButtonLabel: t('general.delete'),
      declineButtonLabel: t('general.cancel'),
      theme: 'danger',
    });
    if (!confirmed) return false;

    await catchError(async () => {
      await deleteReportSubjectApi({ reportId, reportSubjectId });
      await refresh();
      editSuccessToast();
      trackEvent(AnalyticsEvents.report.updated);
    });
    return true;
  };

  const getNoteSubjectIds = async (params: {
    startDate?: string | null;
    endDate?: string | null;
    studentIds: number[];
  }): Promise<Components.Schemas.NoteSubjectIdsDto> => {
    if (!params.studentIds.length || !currentClass.value) return { subjectIds: [], hasNotesWithoutSubject: false };

    return fetchNoteSubjectIds({
      startDate: params.startDate ?? undefined,
      endDate: params.endDate ?? undefined,
      studentIds: params.studentIds,
      schoolClassId: currentClass.value.id,
    });
  };

  const getObservedSubjectIds = async (params: {
    startDate?: string | null;
    endDate?: string | null;
    studentIds: number[];
  }): Promise<number[]> => {
    if (!params.studentIds.length) return [];
    if (!currentClass.value) return [];

    return fetchObservedSubjectIds({
      startDate: params.startDate ?? undefined,
      endDate: params.endDate ?? undefined,
      studentIds: params.studentIds,
      schoolClassId: currentClass.value.id,
    });
  };

  const canReportBeCreated = async (): Promise<Components.Schemas.EReportDataStatus | null> => {
    if (!currentClass.value) return null;

    return fetchCanReportBeCreated({ schoolClassId: currentClass.value.id });
  };

  return {
    closeReport,
    confirmCloseReport,
    createBulkReports,
    createBulkReportsAsync,
    deleteReport,
    getReports,
    totalReports,
    isLoading,
    currentPage,
    pageSize,
    setPage,
    setPageSize,
    getReport,
    searchText,
    statusFilter,
    sortOption,
    setSearchText,
    setStatusFilter,
    setSortOption,
    refresh,
    reports,
    updateReport,
    updateReportComment,
    updateReportCompetence,
    updateReportSubject,
    groupIdFilter,
    setGroupIdFilter,
    studentIdFilter,
    setStudentIdFilter,
    getDefaultReportConfiguration,
    getSelectedReportConfiguration,
    deleteReportSubject,
    getObservedSubjectIds,
    getNoteSubjectIds,
    canReportBeCreated,
  };
};
